import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { AttachmentResponse } from '@pixacare/dtos';
import { AttachmentHttpService } from '@services/http/attachment.http.service';
import { ActionStatus } from '@pixacare/shared';
import { catchError, concat, concatMap, from, mergeMap, Observable, of, switchMap } from 'rxjs';
import { UploadingFile } from '../../models/attachments/uploading-file';
import { attachmentsActions } from './attachments.actions';

@Injectable()
export class AttachmentsEffects {
  private readonly actions$ = inject(Actions);
  private readonly attachmentService = inject(AttachmentHttpService);

  uploadAttachments$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsActions.uploadAttachments),
      mergeMap(({ clientCode, patientId, uploadingFiles }) =>
        from(uploadingFiles).pipe(
          mergeMap(({ id: uploadingFileId, file }: UploadingFile) =>
            concat(
              of(
                attachmentsActions.updateUploadingFileState({
                  clientCode,
                  patientId,
                  uploadingFileId,
                  uploadState: ActionStatus.IN_PROGRESS,
                }),
              ),
              this.attachmentService.uploadAttachment({ clientCode, patientId, file }).pipe(
                concatMap((attachment: AttachmentResponse) => [
                  attachmentsActions.updateUploadingFileState({
                    clientCode,
                    patientId,
                    uploadingFileId,
                    uploadState: ActionStatus.SUCCESS,
                  }),
                  attachmentsActions.removeUploadingFile({
                    clientCode,
                    patientId,
                    uploadingFileId,
                  }),
                  attachmentsActions.uploadAttachmentsSuccess({
                    clientCode,
                    patientId,
                    attachments: [attachment],
                  }),
                ]),
                catchError((error: HttpErrorResponse) =>
                  of(
                    attachmentsActions.updateUploadingFileState({
                      clientCode,
                      patientId,
                      uploadingFileId,
                      uploadState: ActionStatus.ERROR,
                      errorCode: error.error?.customErrorCode,
                    }),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  getPatientAttachments$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsActions.getPatientAttachments),
      mergeMap(({ clientCode, patientId }) =>
        this.attachmentService.getPatientAttachment({ clientCode, patientId }).pipe(
          switchMap((attachments: AttachmentResponse[]) => [
            attachmentsActions.getPatientAttachmentsSuccess({
              clientCode,
              patientId,
              attachments,
            }),
          ]),
        ),
      ),
    ),
  );

  deleteAttachment$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsActions.deleteAttachment),
      mergeMap(({ clientCode, patientId, attachmentId }) =>
        this.attachmentService.deleteAttachment({ clientCode, patientId, attachmentId }).pipe(
          switchMap(() => [
            attachmentsActions.deleteAttachmentSuccess({
              clientCode,
              patientId,
              attachmentId,
            }),
          ]),
        ),
      ),
    ),
  );
}
